import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class SamplePositioningApproachRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.SamplePositioningApproach', 'SamplePositioningApproach');
  }

  protected validate({ args, routine, ctx }: CanonicalValidateArgs): void {
    type SPAArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'SamplePositioningApproach' }
    >;
    const a = args as SPAArgs;
    const spaceItems = this.getSpaceItems(routine, ctx);

    this.checkPoseSource(a, spaceItems, 'target');
    this.checkPoseSource(a, spaceItems, 'starting');
    this.checkAxis(a, spaceItems);

    // narrow/wide radii only apply in linear mode; per-level mode validates the
    // explicit level stack instead.
    if (a.radiusMode === 'perLevel') {
      this.checkLevels(a);
    } else {
      this.checkRadii(a);
    }
  }

  private resolveEffectivePose(
    a: {
      targetSourceKind?: 'singlePosition' | 'positionList';
      targetPositionListID?: string | null;
      targetPose?: { x?: number; y?: number; z?: number } | null;
      startingSourceKind?: 'singlePosition' | 'positionList';
      startingPositionListID?: string | null;
      startingPose?: { x?: number; y?: number; z?: number } | null;
    },
    spaceItems: Array<{
      id: string;
      positions?: Array<{ pose?: { x?: number; y?: number; z?: number } }>;
    }>,
    which: 'target' | 'starting',
  ): { x?: number; y?: number; z?: number } | null {
    const sourceKind =
      which === 'target' ? a.targetSourceKind : a.startingSourceKind;

    if (sourceKind === 'positionList') {
      const id =
        which === 'target' ? a.targetPositionListID : a.startingPositionListID;

      if (!id) return null;
      const item = spaceItems.find((s) => s.id === id);

      return item?.positions?.[0]?.pose ?? null;
    }

    return (which === 'target' ? a.targetPose : a.startingPose) ?? null;
  }

  private checkPoseSource(
    a: {
      targetSourceKind?: 'singlePosition' | 'positionList';
      targetPositionListID?: string | null;
      targetPose?: unknown;
      startingSourceKind?: 'singlePosition' | 'positionList';
      startingPositionListID?: string | null;
      startingPose?: unknown;
    },
    spaceItems: Array<{
      id: string;
      positions?: Array<{ pose?: { x?: number; y?: number; z?: number } }>;
    }>,
    which: 'target' | 'starting',
  ): void {
    const sourceKind =
      which === 'target' ? a.targetSourceKind : a.startingSourceKind;

    const fieldId = which === 'target' ? 'targetPose' : 'startingPose';
    const label = which === 'target' ? 'Target' : 'Starting';

    if (sourceKind === 'positionList') {
      const id =
        which === 'target' ? a.targetPositionListID : a.startingPositionListID;

      if (!id) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `${label} position list must be set`,
          fieldId,
        });
      }

      const item = spaceItems.find((s) => s.id === id);

      if (!item) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `${label} position list no longer exists`,
          fieldId,
        });
      }

      // Mirrors what RoutineFrame.getPositionListEntry enforces at play
      // time — an empty list throws "Position list X is empty". Catch it
      // here so the step fails validation instead of at runtime.
      if (!item.positions?.[0]?.pose) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `${label} position list has no positions`,
          fieldId,
        });
      }

      return;
    }

    const pose = which === 'target' ? a.targetPose : a.startingPose;

    if (!pose) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `${label} pose is not configured`,
        fieldId,
      });
    }
  }

  private checkAxis(
    a: {
      targetSourceKind?: 'singlePosition' | 'positionList';
      targetPositionListID?: string | null;
      targetPose?: { x?: number; y?: number; z?: number } | null;
      startingSourceKind?: 'singlePosition' | 'positionList';
      startingPositionListID?: string | null;
      startingPose?: { x?: number; y?: number; z?: number } | null;
    },
    spaceItems: Array<{
      id: string;
      positions?: Array<{ pose?: { x?: number; y?: number; z?: number } }>;
    }>,
  ): void {
    // Resolve effective poses considering Space references, so config-time
    // validation matches what the runner will compute at play time.
    const target = this.resolveEffectivePose(a, spaceItems, 'target');
    const starting = this.resolveEffectivePose(a, spaceItems, 'starting');

    if (
      target &&
      starting &&
      target.x === starting.x &&
      target.y === starting.y &&
      target.z === starting.z
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'Starting position must differ from target position to define an approach axis',
        fieldId: 'startingPose',
      });
    }
  }

  private checkRadii(a: { narrowRadius?: number; wideRadius?: number }): void {
    if (
      a.narrowRadius != null &&
      a.wideRadius != null &&
      a.wideRadius < a.narrowRadius
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Wide radius must be greater than or equal to narrow radius',
        fieldId: 'wideRadius',
      });
    }
  }

  private checkLevels(a: {
    levels?: Array<{ radius?: number; height?: number }>;
  }): void {
    const { levels } = a;

    if (!Array.isArray(levels) || levels.length < 1) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Per-level mode requires at least one level',
        fieldId: 'levels',
      });
    }

    if (levels.some((l) => typeof l.height !== 'number' || !(l.height > 0))) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Each level height must be greater than 0',
        fieldId: 'levels',
      });
    }
  }
}

export const SamplePositioningApproachValidation: ValidationRule =
  new SamplePositioningApproachRule().toValidationRule();
